import {ILinksNavigationService, INavigateLinkOptions, IOpenUrlOptions} from "./links-navigation.service.interface";
import {ServiceBase} from "../ServiceBase";
import {NavigationLinkBasicOpenMode} from "../configurations/config-data-sections/navigation-link.configuration";
import {LinkConfigurationParser} from "./link-configuration-parser";
import {isAbsolutUrl} from "../../../utils/url-utils";
import {LinkNavigationId} from "../configurations/config-data-sections/generated-config-const";
import {RegulationNavigationHookContinuationMode} from "./regulation-hooks/regulation-link-navigation-hook.interface";


export class LinksNavigationService extends ServiceBase implements ILinksNavigationService {

    getLinkUrl(linkId: LinkNavigationId): string {
        return new LinkConfigurationParser(linkId, this.services).url;
    }

    private _openBlankWindow(openMode: NavigationLinkBasicOpenMode): Window | null {
        if(openMode === "newTab" && !this.services.device.hybrid.isHybrid && !this.services.miniOrbit.isEnabled) {
            // We do this because we try to avoid browser popup blockers.
            try {
                return this.services.window.createBlankWindow();
            } catch (err) {
                this.services.logger.error('Failed to create blank window', err);
                return null;
            }

        }
        return null;
    }

    async navigateLink(options: INavigateLinkOptions): Promise<void> {
        const hookResponse = await this.services.regulation.current.linksNavigationHook.navigateLink(options);
        if(hookResponse.continuationMode !== RegulationNavigationHookContinuationMode.Continue) {
            return;
        }

        options = hookResponse.options ?? options;

        const config = new LinkConfigurationParser(options.linkId, this.services);

        const openMode = this._getLinkOpenMode(config);

        let blankBrowserTab = this._openBlankWindow(openMode);

        let link = await this._buildLink(config, options);

        if(options.linkHash) {
            let linkHash = options.linkHash;
            if(!linkHash.startsWith('#')) {
                linkHash = '#' + linkHash;
            }
            link = link + linkHash;
        }

        await this._navigateToUrl(link, openMode, blankBrowserTab);
    }


    async openUrl(options: IOpenUrlOptions): Promise<void> {

        let openMode: NavigationLinkBasicOpenMode;
        if(options.openMode) {
            openMode = options.openMode
        } else {
            if(isAbsolutUrl(options.url)) {
                openMode = "newTab";
            } else {
                openMode = "clientSideNavigation";
            }
        }

        await this._navigateToUrl(options.url, openMode, this._openBlankWindow(openMode));
    }

    private _openInNewTabForMiniOrbit(url: string): void {
        this.services.miniOrbit.openUrl(url);
    }

    private _openInNewTabForHybrid(url: string): void {
        this.services.hybrid.openExternalLink(url);
    }

    private _openInNewTabForWebApp(url: string, blankBrowserTab: Window | null) {
        if(!isAbsolutUrl(url)) {
            const urlBuilder = new URL(this.services.window.origin);
            urlBuilder.pathname = url;
            url = urlBuilder.href;
        }

        if (blankBrowserTab) {
            blankBrowserTab.location = url;
        } else {
            this.services.window.openInNewTab(url);
        }
    }

    private async _redirectForMiniOrbit(url: string): Promise<void> {
        this.services.sdk.eventsTrigger.closeNyxSecondWebView();
        await this.services.location.redirect(url);
    }

    private async _redirectForHybrid(url: string): Promise<void> {
        this.services.sdk.eventsTrigger.closeNyxSecondWebView();
        await this.services.location.redirect(url);
    }

    private async _navigateToUrl(url: string, openMode: NavigationLinkBasicOpenMode, blankBrowserTab: Window | null): Promise<void> {
        switch (openMode) {
            case "clientSideNavigation":
                await this.services.location.push(url.toLowerCase());
                break;
            case "newTab":
                if(this.services.miniOrbit.isEnabled) {
                    this._openInNewTabForMiniOrbit(url);
                } else if(this.services.device.hybrid.isHybrid) {
                    this._openInNewTabForHybrid(url);
                } else {
                    this._openInNewTabForWebApp(url, blankBrowserTab);
                }
                break;
            case "redirect":
                if(this.services.miniOrbit.isEnabled) {
                    await this._redirectForMiniOrbit(url);
                } else if(this.services.device.hybrid.isHybrid)  {
                    await this._redirectForHybrid(url);
                } else {
                    await this.services.location.redirect(url);
                }
                break;
            case "secondWebView":
                this.services.sdk.eventsTrigger.openUrlInSecondWebView(url);
                break;
            default:
                throw new Error(`Unsupported external brand open mode ${openMode}`)

        }
    }

    protected async _buildDirectLink(config: LinkConfigurationParser): Promise<string> {

        let directLink = config.url;

        if(this._getLinkOpenMode(config) !== "clientSideNavigation") {

            if(!isAbsolutUrl(directLink)) {
                const urlBuilder = new URL(this.services.window.origin);
                urlBuilder.pathname = directLink;
                directLink = urlBuilder.href;
            }

            if(config.shouldAppendSCutToUrl) {
                directLink = (await this.services.marketing).appendSCutToUrl(directLink);
            }

            directLink = this.services.stateOfResidenceIdentifier.appendUserSelectedStateToLink(directLink);
        }

        return directLink;
    }

    protected async _buildLink(config: LinkConfigurationParser, options: INavigateLinkOptions): Promise<string> {

        if(this._isCrossBackToPokerOrSport(config)) {
            const token = await this.services.user.getPlatformToken();
            return `${this.services.crossBrands.crossBackSpecificNavigation.callbackUrl!}?UserInfo=${token}`;
        }

        if(config.urlBuilderStrategy === "useUrl") {
            return await this._buildDirectLink(config);
        } else {
            return await config.getGnwpUrl(options.marketingParams);
        }
    }

    private _isCrossBackToPokerOrSport(config: LinkConfigurationParser): boolean {
        if(!this.services.user.isAuthorized) {
            return false;
        }

        return config.isCrossBackToPokerOrSport;
    }

    private _getCrossBackMode(config: LinkConfigurationParser): NavigationLinkBasicOpenMode {
        if(this.services.device.hybrid.isHybrid) {
            return config.crossBackMode.hybrid;
        }

        if(this.services.device.os.isMobileOS) {
            return config.crossBackMode.mobile;
        }

        return config.crossBackMode.pc;
    }

    protected _getLinkOpenMode(config: LinkConfigurationParser): NavigationLinkBasicOpenMode {


        if(this._isCrossBackToPokerOrSport(config)) {
            return this._getCrossBackMode(config);
        }

        if(this.services.miniOrbit.isEnabled) {
            return config.openMode.miniOrbit;
        }

        if(this.services.device.hybrid.isHybrid) {
            return config.openMode.hybrid;
        }

        if(this.services.device.os.isMobileOS) {
            return config.openMode.mobile;
        }

        return config.openMode.pc;
    }


    async openPoker(): Promise<void> {
        await this.navigateLink({
            linkId: "pokerLink"
        });
    }

    async openSport(): Promise<void> {
        await this.navigateLink({
            linkId: "sportLink"
        });
    }

    async openResponsibleGaming(): Promise<void> {
        await this.navigateLink({
            linkId: "responsibleGamingLink"
        });
    }

    async openSecondaryResponsibleGaming(): Promise<void> {
        await this.navigateLink({
            linkId: "secondaryResponsibleGamingLink"
        });
    }

    async openGamingHistoryPage(): Promise<void> {
        await this.navigateLink({
            linkId: "gamingHistoryLink"
        });
    }

    async openSelfExclusion(): Promise<void> {
        await this.navigateLink({
            linkId: "selfExclusionLink"
        });
    }

    async openSelfTest(): Promise<void> {
        await this.navigateLink({
            linkId: "selfTestLink"
        });
    }

    async openSafeToPlay(): Promise<void> {
        await this.navigateLink({
            linkId: "safeToPlayLink"
        });
    }

    async openGamblingCommissionLink(): Promise<void> {
        await this.navigateLink({
            linkId: "gamblingCommissionLink"
        });
    }
}